import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Coins,
  Languages,
  RefreshCw,
  Search,
  Sparkles,
} from "lucide-react";
import { t } from "@/i18n";

/* Trois points = trois bonnes réponses avant qu'un mot soit "appris". */
function WordGauge({ box = 0 }) {
  const filled = Math.min(3, Math.max(0, Number(box) || 0));
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-3 rounded-full ${
            i < filled ? "bg-green-500" : "bg-muted-foreground/20"
          }`}
        />
      ))}
    </div>
  );
}

function WordCard({ word }) {
  const box = Number(word.box) || 0;
  const status = box >= 3 ? "mastered" : box > 0 ? "learning" : "new";
  const statusLabel =
    status === "mastered"
      ? "Appris"
      : status === "learning"
        ? "En cours"
        : "À découvrir";
  const statusColor =
    status === "mastered"
      ? "text-green-600 bg-green-50 dark:bg-green-950/30"
      : status === "learning"
        ? "text-amber-600 bg-amber-50 dark:bg-amber-950/30"
        : "text-muted-foreground bg-secondary/50";

  return (
    <div className="rounded-2xl border bg-card p-4 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 mb-1 flex-wrap">
          <span className="arabic text-2xl font-bold" dir="rtl">
            {word.word_ar}
          </span>
          {word.word_latin && (
            <span className="text-sm text-muted-foreground italic">
              {word.word_latin}
            </span>
          )}
        </div>
        <div className="text-sm font-medium">{word.translation_en}</div>
        {word.example_fr && (
          <div className="text-xs text-muted-foreground mt-1 truncate">
            {word.example_fr}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <span
          className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${statusColor}`}
        >
          {statusLabel}
        </span>
        <WordGauge box={box} />
      </div>
    </div>
  );
}

/* Bandeau d'erreur explicite. L'ancienne version de cette page retombait en
   silence sur une liste vide quand la requête échouait — la page restait
   quasi nue, sans navbar visible non plus qu'un texte disant pourquoi. On
   affiche maintenant toujours ce qui s'est passé, en clair, sur la page. */
function ErrorPanel({ detail, onRetry }) {
  return (
    <div className="rounded-2xl border-2 border-red-500/30 bg-red-50/60 dark:bg-red-950/20 p-6 text-center">
      <AlertTriangle size={32} className="mx-auto text-red-500 mb-3" />
      <h2 className="font-head text-lg font-bold mb-1">
        {t("vocabgroupwords.impossible_de_charger_ce_groupe")}
      </h2>
      <p className="text-sm text-muted-foreground mb-4">{detail}</p>
      <button
        onClick={onRetry}
        className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-2 mx-auto hover:opacity-90 transition"
      >
        <RefreshCw size={15} /> {t("vocabgroupwords.reessayer")}
      </button>
    </div>
  );
}

export default function VocabGroupWords() {
  const { groupId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    setData(null);
    setError(null);
    api
      .get(`/progression/vocab-groups/${encodeURIComponent(groupId)}/words`)
      .then((r) => {
        const body = r.data;
        // Si le serveur répond mais pas avec la forme attendue (par exemple
        // une page HTML renvoyée par erreur au lieu de JSON), on le signale
        // clairement plutôt que d'afficher une page à moitié vide.
        if (!body || typeof body !== "object" || !Array.isArray(body.words)) {
          setError(
            "Le serveur a répondu, mais pas avec les données attendues. " +
              "Le fichier progression.py déployé n'est peut-être pas la " +
              "dernière version — redépose-le puis redémarre le serveur.",
          );
          return;
        }
        setData(body);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404) {
          setError(
            "Ce groupe n'existe pas ou plus. Retourne au parcours et " +
              "ouvre-le depuis la liste des groupes.",
          );
        } else if (status === 401 || status === 403) {
          setError("Ta session a expiré. Reconnecte-toi puis réessaie.");
        } else if (status) {
          setError(`Le serveur a répondu avec une erreur (code ${status}).`);
        } else {
          setError(
            "Aucune réponse du serveur. La route de l'API n'est peut-être " +
              "pas encore déployée, ou le serveur backend est arrêté.",
          );
        }
      });
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => nav("/roadmap")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft size={16} /> {t("moduletest.retour_au_parcours")}
        </button>
        <ErrorPanel detail={error} onRetry={load} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-muted-foreground">
        {t("vocabgroupwords.chargement_du_groupe")} {groupId} »…
      </div>
    );
  }

  const words = (data.words || []).filter((w) => {
    const box = Number(w.box) || 0;
    if (filter === "mastered" && box < 3) return false;
    if (filter === "learning" && (box === 0 || box >= 3)) return false;
    if (filter === "new" && box > 0) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (w.word_latin || "").toLowerCase().includes(q) ||
      (w.translation_en || "").toLowerCase().includes(q) ||
      (w.word_ar || "").includes(q)
    );
  });

  const total = data.total ?? data.words.length;
  const mastered =
    data.mastered ?? data.words.filter((w) => (Number(w.box) || 0) >= 3).length;
  const pct = data.pct ?? (total ? Math.round((100 * mastered) / total) : 0);
  const gatePct = data.gate_pct ?? 90;
  const title = data.title || groupId;
  const validated = pct >= gatePct;

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={() => nav("/roadmap")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft size={16} /> {t("moduletest.retour_au_parcours")}
      </button>

      <div className="rounded-3xl border bg-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-sky-500/10 text-sky-500 flex items-center justify-center shrink-0">
            <Languages size={24} />
          </div>
          <div className="min-w-0">
            <h1 className="font-head text-2xl font-bold truncate">{title}</h1>
            <p className="text-sm text-muted-foreground">
              {mastered}/{total} {t("vocabgroupwords.mots_appris")}
            </p>
          </div>
          {validated && (
            <span className="ml-auto shrink-0 flex items-center gap-1 text-sm font-bold text-green-600">
              <CheckCircle2 size={16} /> {t("vocabgroupwords.valide")}
            </span>
          )}
        </div>
        <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-2">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${pct}%`,
              background: validated ? "#16A34A" : "#0EA5E9",
            }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{t("vocabgroupwords.objectif")} {gatePct}{t("vocabgroupwords.de_mots_appris")}</span>
          <span className="flex items-center gap-1 font-semibold text-amber-600">
            <Coins size={13} />
            {validated ? t("vocabgroupwords.50_recus") : t("vocabgroupwords.50_a_la_cle")}
          </span>
        </div>
        <button
          onClick={() =>
            nav(`/practice/vocab?group=${encodeURIComponent(groupId)}`)
          }
          className="w-full mt-5 py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 hover:opacity-90 transition"
        >
          <Sparkles size={18} /> {t("vocabgroupwords.s_entrainer_sur_ce_groupe")}
        </button>
      </div>

      {total === 0 ? (
        <div className="rounded-2xl border bg-card p-8 text-center text-muted-foreground text-sm">
          {t("vocabgroupwords.ce_groupe_ne_contient_encore_aucun")} {groupId} {t("vocabgroupwords.pour_qu_il_se_remplisse")}
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("vocabgroupwords.chercher_un_mot")}
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border bg-card text-sm"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {[
                { id: "all", label: "Tous" },
                { id: "new", label: "À découvrir" },
                { id: "learning", label: "En cours" },
                { id: "mastered", label: "Appris" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition ${
                    filter === f.id
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {words.length === 0 ? (
            <div className="rounded-2xl border bg-card p-8 text-center text-muted-foreground text-sm">
              {t("vocabgroupwords.aucun_mot_ne_correspond_a_cette")}
            </div>
          ) : (
            <div className="space-y-2">
              {words.map((w) => (
                <WordCard key={w.id} word={w} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
